import { useLanguage } from '@/contexts/LanguageContext';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ArrowRight } from 'lucide-react';

const About = () => {
  const { t, language } = useLanguage();
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.08 });

  const partners = [
    { name: 'Jaguar Land Rover', src: '/partners/jaguar-land-rover.png', fallback: '/partners/jlr.png' },
    { name: 'Volkswagen', src: '/partners/volkswagen.png', fallback: '/partners/vw.png' },
    { name: 'Škoda Auto', src: '/partners/skoda.png', fallback: '/partners/skoda-auto.png' },
  ];

  return (
    <section id="about" ref={ref} className="section-pad bg-background relative">
      <div className="container-tight">
        {/* Section header — editorial rhythm */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-20">
          <div className="lg:col-span-4">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6 }}
              className="sticky top-32"
            >
              <span className="eyebrow mb-6 block">{t('about.badge')}</span>
              <h2 className="font-display font-bold text-primary" style={{ fontSize: 'clamp(2.25rem, 4vw, 3.5rem)', lineHeight: 1.02 }}>
                {t('about.title')}
              </h2>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="lg:col-span-7 lg:col-start-6 pt-2"
          >
            <h3 className="font-display text-2xl md:text-3xl font-semibold text-primary mb-6">
              {t('about.story.title')}
            </h3>
            <div className="space-y-5 mb-8">
              {t('about.story.description').split('\n\n').map((para, i) => (
                <p key={i} className="lede">
                  {para}
                </p>
              ))}
            </div>
            <a
              href="https://www.tataautocomp.com/artifex/"
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-2 font-display text-sm font-semibold text-primary hover:text-secondary transition-colors mb-8"
            >
              {t('about.group.cta')}
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </a>
            <div className="hairline" />
          </motion.div>
        </div>

        {/* Vision & Mission — clean two-column */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[hsl(var(--hairline))] mb-24 border border-[hsl(var(--hairline))] rounded-xl overflow-hidden"
        >
          <div className="bg-background p-10 md:p-12 group transition-colors duration-300 hover:bg-muted/40">
            <div className="flex items-start justify-between mb-6">
              <span className="font-display text-xs tabular-nums tracking-[0.22em] text-muted-foreground">01</span>
              <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-secondary transition-colors" />
            </div>
            <h3 className="font-display text-2xl md:text-3xl font-semibold text-primary mb-5">
              {t('about.vision.title')}
            </h3>
            <p className="text-foreground/75 leading-relaxed">
              {t('about.vision.description')}
            </p>
          </div>
          <div className="bg-background p-10 md:p-12 group transition-colors duration-300 hover:bg-muted/40">
            <div className="flex items-start justify-between mb-6">
              <span className="font-display text-xs tabular-nums tracking-[0.22em] text-muted-foreground">02</span>
              <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-secondary transition-colors" />
            </div>
            <h3 className="font-display text-2xl md:text-3xl font-semibold text-primary mb-5">
              {t('about.mission.title')}
            </h3>
            <p className="text-foreground/75 leading-relaxed">
              {t('about.mission.description')}
            </p>
          </div>
        </motion.div>

        {/* Values illustration */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mb-24"
        >
          <div className="flex items-end justify-between mb-10">
            <div>
              <h3 className="font-display text-3xl md:text-4xl font-semibold text-primary">
                {t('about.values.title')}
              </h3>
            </div>
            <div className="h-px w-24 md:w-40 bg-[hsl(var(--hairline-strong))] mb-3 hidden sm:block" />
          </div>
          <div className="rounded-xl overflow-hidden border border-[hsl(var(--hairline))] bg-muted/30">
            <img
              src={language === 'sk' ? '/image.png' : '/values-en.jpg'}
              alt={t('about.values.title')}
              className="w-full h-auto object-contain"
            />
          </div>
        </motion.div>

        {/* Partners — minimalist row */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="mb-24"
        >
          <div className="flex items-end justify-between mb-10">
            <div>
              <h3 className="font-display text-3xl md:text-4xl font-semibold text-primary">
                {t('about.partners.title')}
              </h3>
              <p className="text-muted-foreground mt-3 max-w-xl">
                {t('about.partners.description')}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-[hsl(var(--hairline))] border border-[hsl(var(--hairline))] rounded-xl overflow-hidden">
            {partners.map((p) => (
              <div
                key={p.name}
                className="bg-background h-40 flex items-center justify-center p-10 group transition-colors hover:bg-muted/40"
              >
                <img
                  src={p.src}
                  alt={p.name}
                  className="max-h-16 max-w-full object-contain grayscale opacity-60 group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-500"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = p.fallback;
                  }}
                />
              </div>
            ))}
          </div>
        </motion.div>

        {/* Commitment — split with video */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start"
        >
          <div className="lg:col-span-5">
            <h3 className="font-display text-3xl md:text-4xl font-semibold text-primary mb-8">
              {t('about.commitment.title')}
            </h3>
            <div className="space-y-6">
              {[t('about.commitment.p1'), t('about.commitment.p2'), t('about.commitment.p3')].map((p, i) => (
                <div key={i} className="flex gap-5 pt-5 border-t border-[hsl(var(--hairline))]">
                  <span className="section-num shrink-0 pt-1">0{i + 1}</span>
                  <p className="text-foreground/75 leading-relaxed">{p}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="aspect-video rounded-xl overflow-hidden border border-[hsl(var(--hairline))] bg-primary">
              <iframe
                className="w-full h-full"
                src="https://www.youtube.com/embed/Ne4V2H4AbHE"
                title="Artifex Systems Slovakia"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;
